import { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, AlertTriangle, CreditCard, ArrowRight, Loader2, ShieldCheck } from 'lucide-react';
import { Logo } from '../components/Logo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public worker-facing penalty payment page.
 * No auth required — link is shared via Resend email + in-app notification.
 * - GET /api/penalties/pay/public/{penalty_id} → status + Stripe link
 * - POST /api/penalties/pay/verify/{penalty_id}?session_id=... → after success redirect
 */
export default function PenaltyPaymentPage() {
  const { penaltyId } = useParams();
  const [params] = useSearchParams();
  const [penalty, setPenalty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState(null);
  const cancelled = params.get('cancelled') === '1';

  const load = useCallback(async () => {
    try {
      setError(null);
      const res = await axios.get(`${API}/penalties/pay/public/${penaltyId}`);
      setPenalty(res.data);
    } catch (e) {
      setError(e.response?.status === 404 ? 'Penalty not found' : 'Could not load penalty details');
    } finally {
      setLoading(false);
    }
  }, [penaltyId]);

  useEffect(() => { load(); }, [load]);

  // If we're on the SAME page after Stripe success (legacy fallback), no-op.
  // The dedicated /penalty-pay/result page handles verification.

  const goToStripe = () => {
    if (penalty?.stripePaymentLinkUrl) {
      window.location.href = penalty.stripePaymentLinkUrl;
    }
  };

  const isPaid = penalty?.penaltyStatus === 'paid';
  const isWaived = penalty?.penaltyStatus === 'waived';

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="penalty-payment-page">
      <header className="bg-[#0B132B] text-white">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo size="sm" variant="light" linkTo="/" />
          <Link to="/" className="text-[12.5px] text-white/70 hover:text-white transition-colors">Home</Link>
        </div>
      </header>

      <main className="flex-1">
        <div className="max-w-2xl mx-auto px-6 py-14 sm:py-20">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#DA291C] mb-3">Penalty Payment</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 leading-tight">
            Settle your penalty
          </h1>
          <p className="mt-3 text-[14.5px] text-slate-600 leading-relaxed">
            Pay securely via Stripe. ShiftCover365 never sees your card details.
          </p>

          {/* Body */}
          <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8" data-testid="penalty-payment-card">
            {loading ? (
              <Center><Loader2 className="h-5 w-5 animate-spin text-slate-500" /></Center>
            ) : error ? (
              <ErrorState text={error} />
            ) : isPaid ? (
              <SuccessState amount={penalty.amount} />
            ) : isWaived ? (
              <InfoState
                title="This penalty has been waived"
                body="Your manager has waived this penalty — no payment is required."
              />
            ) : (
              <>
                {cancelled && (
                  <div className="mb-5 flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 p-3.5">
                    <AlertTriangle className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
                    <p className="text-[13px] text-amber-900 leading-relaxed">
                      Payment was cancelled. You can try again below.
                    </p>
                  </div>
                )}

                <dl className="grid grid-cols-3 gap-x-4 gap-y-3 text-[13.5px] mb-6">
                  <Row label="Amount" value={<span className="text-2xl font-bold text-slate-900 tabular-nums">£{Number(penalty.amount || 0).toFixed(2)}</span>} />
                  <Row label="Reason" value={penalty.reason || '—'} />
                  {penalty.shiftBranch && <Row label="Branch" value={penalty.shiftBranch} />}
                  {penalty.shiftDate && <Row label="Shift date" value={penalty.shiftDate} />}
                  <Row label="Status" value={
                    <span className="inline-flex items-center rounded-full bg-amber-50 text-amber-700 border border-amber-100 text-[11.5px] font-semibold px-2.5 h-6">
                      {penalty.penaltyStatus}
                    </span>
                  } />
                </dl>

                <button
                  onClick={goToStripe}
                  disabled={!penalty.stripePaymentLinkUrl || verifying}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#DA291C] hover:bg-[#b91f13] text-white px-6 h-12 text-[14px] font-semibold transition-colors shadow-[0_8px_24px_-6px_rgba(218,41,28,0.4)] disabled:opacity-60"
                  data-testid="penalty-pay-stripe-btn"
                >
                  <CreditCard className="h-4 w-4" />
                  Pay £{Number(penalty.amount || 0).toFixed(2)} via Stripe
                  <ArrowRight className="h-4 w-4" />
                </button>

                <p className="mt-4 text-[12px] text-slate-500 flex items-center gap-1.5 justify-center">
                  <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
                  Card details handled by Stripe — never stored on ShiftCover365.
                </p>
              </>
            )}
          </div>

          <p className="mt-8 text-[12.5px] text-slate-500 leading-relaxed">
            If you believe this penalty was issued in error, contact your manager directly or email{' '}
            <a href="mailto:support@shiftcover365.co.uk" className="font-semibold text-slate-700 hover:text-slate-900">support@shiftcover365.co.uk</a>.
          </p>
        </div>
      </main>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <>
      <dt className="col-span-1 text-slate-500 font-medium pt-1.5">{label}</dt>
      <dd className="col-span-2 text-slate-900">{value}</dd>
    </>
  );
}

function Center({ children }) {
  return <div className="flex items-center justify-center py-12">{children}</div>;
}

function ErrorState({ text }) {
  return (
    <div className="flex flex-col items-center text-center py-8">
      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-red-50 border border-red-200 mb-3">
        <AlertTriangle className="h-5 w-5 text-red-600" />
      </span>
      <p className="text-[14.5px] font-semibold text-slate-900">{text}</p>
    </div>
  );
}

function SuccessState({ amount }) {
  return (
    <div className="flex flex-col items-center text-center py-8">
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 border border-emerald-200 mb-4">
        <CheckCircle2 className="h-6 w-6 text-emerald-600" />
      </span>
      <p className="text-[16px] font-bold text-slate-900">Penalty paid</p>
      <p className="text-[13.5px] text-slate-500 mt-1">
        Your £{Number(amount || 0).toFixed(2)} penalty has been settled successfully. Thank you.
      </p>
    </div>
  );
}

function InfoState({ title, body }) {
  return (
    <div className="flex flex-col items-center text-center py-8">
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 border border-slate-200 mb-4">
        <ShieldCheck className="h-6 w-6 text-slate-600" />
      </span>
      <p className="text-[16px] font-bold text-slate-900">{title}</p>
      <p className="text-[13.5px] text-slate-500 mt-1">{body}</p>
    </div>
  );
}
